import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Table, TableBody, TableHead, TableRow, Typography } from '@mui/material';
import {
  CustomAutoComplete,
  CustomTextArea,
  CustomTextField,
  DialogActions,
  DialogBody,
  DialogContainer,
  DialogTitle,
  GeneralTableBox,
  HeadTableCell,
  renderTwoLineOption,
  RowTableCell,
} from '../../../common/components';
import { AUTOCOMPLETE_LISTBOX_PROPS } from '../../../common/styles';
import { useGenerateConsumptionFromOrder } from '../../../hooks/useConsumptions';
import {
  CONSUMPTION_TABLE_TEXTS,
  GENERATE_CONSUMPTION_TEXTS as TEXTS,
  INITIAL_GENERATE_CONSUMPTION_STATE,
} from '../constants/consumptionsConstants';
import {
  buildGenerateConsumptionPayload,
  formatConsumptionQuantity,
  getConsumptionApiErrorMessage,
  getItemConsumableQuantity,
  getReadyToConsumeOrderItems,
  isPartialConsumptionScenario,
} from '../utils/consumptionsUtils';
import { useReportPendingState } from '../../hooks/useReportPendingState';
import { guardDialogClose } from '../../utils/actionLockUtils';
import { toWarehouseOptions } from '../../utils/toWarehouseOptions';
import { getOrderItemArticleParts } from '../utils/getOrderItemArticleParts';

import ClientPaginatedTable from '../../../common/components/table/ClientPaginatedTable';
const textSx = { textAlign: 'left' };
const numberSx = { textAlign: 'right' };

const MIN_TABLE_WIDTH = 560;

const ITEM_COLUMNS = [
  { key: 'article', label: CONSUMPTION_TABLE_TEXTS.article, sx: textSx },
  { key: 'requested', label: CONSUMPTION_TABLE_TEXTS.requested, sx: { ...numberSx, width: 120 } },
  { key: 'missing', label: CONSUMPTION_TABLE_TEXTS.missing, sx: { ...numberSx, width: 120 } },
  { key: 'toConsume', label: CONSUMPTION_TABLE_TEXTS.toConsume, sx: { ...numberSx, width: 130 } },
];

const GenerateConsumptionDialog = ({
  open,
  onClose,
  order,
  warehouses = [],
  defaultWarehouseId = '',
  articlesByUuid = {},
  onSuccess,
  onPendingChange,
}) => {
  const [formData, setFormData] = useState(INITIAL_GENERATE_CONSUMPTION_STATE);
  const [errorMessage, setErrorMessage] = useState('');
  const generateMutation = useGenerateConsumptionFromOrder();
  useReportPendingState(generateMutation.isPending, onPendingChange);
  const readyItems = useMemo(() => getReadyToConsumeOrderItems(order), [order]);
  const isPartial = useMemo(() => isPartialConsumptionScenario(order), [order]);

  const warehouseOptions = toWarehouseOptions(warehouses);

  useEffect(() => {
    if (open) {
      setFormData({
        uuid_warehouse: defaultWarehouseId || order?.uuid_destination_warehouse || '',
        observations: '',
      });
      setErrorMessage('');
    }
  }, [open, order?.uuid, defaultWarehouseId, order?.uuid_destination_warehouse]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleSubmit = async () => {
    if (!order?.uuid) return;

    try {
      setErrorMessage('');
      const payload = buildGenerateConsumptionPayload(formData);
      const consumption = await generateMutation.mutateAsync({
        orderId: order.uuid,
        payload,
      });
      onSuccess?.(consumption);
      onClose();
    } catch (error) {
      setErrorMessage(
        getConsumptionApiErrorMessage(error, 'No se pudo generar el consumo')
      );
    }
  };

  if (!open) return null;

  return (
    <DialogContainer
      open={open}
      onClose={guardDialogClose(generateMutation.isPending, onClose)}
      size="medium"
    >
      <DialogTitle>
        {isPartial ? 'Consumir materiales listos' : 'Generar consumo'}
      </DialogTitle>

      <DialogBody sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        <CustomTextField
          label="Orden técnica"
          name="orderNumber"
          value={order?.number || '-'}
          disabled
        />

        {readyItems.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {TEXTS.materialsTitle}
            </Typography>
            <ClientPaginatedTable rows={readyItems} resetKey={order?.uuid}>
              {(pageRows) => (
                <GeneralTableBox hasMaxHeight={false} fixedLayout minTableWidth={MIN_TABLE_WIDTH}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {ITEM_COLUMNS.map((column) => (
                          <HeadTableCell key={column.key} sx={column.sx}>
                            {column.label}
                          </HeadTableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pageRows.map((item) => {
                        const article = getOrderItemArticleParts(
                          articlesByUuid,
                          item.uuid_inventory_article
                        );
                        return (
                          <TableRow key={item.uuid} hover>
                            <RowTableCell sx={textSx} title={article.name}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 700, lineHeight: 1.25 }}
                                noWrap
                              >
                                {article.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                                {article.code}
                              </Typography>
                            </RowTableCell>
                            <RowTableCell sx={numberSx}>
                              {formatConsumptionQuantity(item.requested_quantity)}
                            </RowTableCell>
                            <RowTableCell sx={numberSx}>
                              {formatConsumptionQuantity(item.missing_quantity)}
                            </RowTableCell>
                            <RowTableCell sx={numberSx}>
                              {formatConsumptionQuantity(getItemConsumableQuantity(item))}
                            </RowTableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </GeneralTableBox>
              )}
            </ClientPaginatedTable>
          </Box>
        )}

        <CustomAutoComplete
          label={TEXTS.warehouse}
          name="uuid_warehouse"
          value={formData.uuid_warehouse}
          options={warehouseOptions}
          renderOption={renderTwoLineOption}
          inputPlaceholder={TEXTS.searchWarehouse}
          ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
          onChange={handleChange}
        />

        <CustomTextArea
          label={TEXTS.observations}
          name="observations"
          value={formData.observations}
          onChange={handleChange}
          minRows={2}
        />

        <Box component="p" sx={{ m: 0, color: 'text.secondary', fontSize: 14 }}>
          {isPartial
            ? 'Se consumirá la cantidad ya disponible de cada material. El faltante seguirá pendiente hasta completar traslado o recepción.'
            : 'Al generar el consumo se descontará el stock del depósito seleccionado. Los artículos y cantidades provienen del pedido técnico y no pueden editarse.'}
        </Box>
      </DialogBody>

      <DialogActions
        handleClose={onClose}
        handleSave={handleSubmit}
        saveLabel={isPartial ? 'Consumir materiales listos' : 'Generar consumo'}
        saveButtonProps={{ isLoading: generateMutation.isPending }}
      />
    </DialogContainer>
  );
};

export default GenerateConsumptionDialog;
